import { clsx } from 'clsx'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react'

export function Field({
  label,
  hint,
  required,
  error,
  children,
  className,
}: {
  label?: ReactNode
  hint?: ReactNode
  required?: boolean
  error?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <label className={clsx('block min-w-0', className)}>
      {label ? (
        <span className="mb-1 flex items-baseline gap-1.5 text-xs font-medium text-ink-2">
          {label}
          {required ? <span className="text-2xs font-semibold text-accent">必須</span> : null}
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="mt-1 block text-2xs text-negative">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-2xs leading-relaxed text-ink-3">{hint}</span>
      ) : null}
    </label>
  )
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx('field', className)} {...rest} />
}

export function TextArea({
  className,
  rows = 4,
  ...rest
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={rows} className={clsx('field resize-y leading-relaxed', className)} {...rest} />
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
  ariaLabel,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<{ value: T; label: string; disabled?: boolean }>
  placeholder?: string
  className?: string
  disabled?: boolean
  ariaLabel?: string
}) {
  return (
    <div className={clsx('relative min-w-0', className)}>
      <select
        aria-label={ariaLabel}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className="field w-full cursor-pointer appearance-none truncate pr-8"
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
        aria-hidden
      />
    </div>
  )
}

/**
 * Typeahead picker for the long lists (accounts, slugs, campaigns) where a
 * native `<select>` turns into a hundreds-of-rows scroll.
 */
export function Combobox<T extends string>({
  value,
  onChange,
  options,
  placeholder = '選択',
  emptyText = '該当なし',
  className,
  ariaLabel,
}: {
  value: T
  onChange: (v: T) => void
  options: Array<{ value: T; label: string; hint?: string }>
  placeholder?: string
  emptyText?: string
  className?: string
  ariaLabel?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const current = options.find((o) => o.value === value)

  const filtered = useMemo(() => {
    const t = q.trim().toLocaleLowerCase('ja-JP')
    if (!t) return options
    return options.filter(
      (o) =>
        o.label.toLocaleLowerCase('ja-JP').includes(t) ||
        String(o.value).toLocaleLowerCase('ja-JP').includes(t),
    )
  }, [q, options])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div ref={boxRef} className={clsx('relative min-w-0', className)}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          setOpen((v) => !v)
          setQ('')
          setActive(0)
        }}
        className="field flex w-full items-center justify-between gap-2 text-left"
      >
        <span className={clsx('truncate', current ? 'text-ink' : 'text-ink-3')}>
          {current?.label ?? placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border border-line bg-surface shadow-pop">
          <div className="border-b border-line p-1.5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value)
                  setActive(0)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setActive((i) => Math.min(i + 1, filtered.length - 1))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActive((i) => Math.max(i - 1, 0))
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    const pick = filtered[active]
                    if (pick) {
                      onChange(pick.value)
                      setOpen(false)
                    }
                  } else if (e.key === 'Escape') {
                    setOpen(false)
                  }
                }}
                placeholder="絞り込み"
                className="w-full rounded-md bg-surface-2 py-1.5 pl-7 pr-2 text-xs text-ink outline-none placeholder:text-ink-3"
              />
            </div>
          </div>
          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-ink-3">{emptyText}</li>
            ) : (
              filtered.map((o, i) => (
                <li key={o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      onChange(o.value)
                      setOpen(false)
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                      i === active ? 'bg-surface-hover text-ink' : 'text-ink-2',
                    )}
                  >
                    <Check
                      className={clsx('h-3.5 w-3.5 shrink-0', o.value === value ? 'text-accent' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {o.hint ? <span className="shrink-0 text-2xs tnum text-ink-3">{o.hint}</span> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export function SearchInput({
  value,
  onChange,
  placeholder = '検索',
  className,
  ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  className?: string
  ariaLabel?: string
}) {
  return (
    <div className={clsx('relative min-w-0', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden />
      <input
        type="search"
        aria-label={ariaLabel ?? placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field w-full pl-9 pr-8 [&::-webkit-search-cancel-button]:hidden"
      />
      {value ? (
        <button
          type="button"
          aria-label="検索条件を消去"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-3 hover:bg-surface-hover hover:text-ink"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  hint?: ReactNode
  disabled?: boolean
}) {
  return (
    <label className={clsx('flex items-start gap-2.5', disabled && 'opacity-55')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          'mt-0.5 h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors',
          checked ? 'bg-accent' : 'bg-line-strong',
        )}
      >
        <span
          className={clsx(
            'block h-4 w-4 rounded-full bg-white shadow-soft transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </button>
      <span className="min-w-0">
        <span className="block text-sm text-ink">{label}</span>
        {hint ? <span className="mt-0.5 block text-2xs leading-relaxed text-ink-3">{hint}</span> : null}
      </span>
    </label>
  )
}

export function Checkbox({
  checked,
  onChange,
  label,
  indeterminate,
  className,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label?: ReactNode
  indeterminate?: boolean
  className?: string
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate)
  }, [indeterminate])
  return (
    <label className={clsx('inline-flex items-center gap-2 text-sm text-ink', className)}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 shrink-0 rounded border-line-strong text-accent accent-[rgb(var(--c-accent))]"
      />
      {label}
    </label>
  )
}
